import React, { useState } from 'react';
import LoadingSpinner from '../shared/LoadingSpinner';
import './GroupSearchModal.css';

const GroupSearchModal = ({ onClose, onSearchByName }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchTerm.trim()) {
      setError('Please enter a group name to search');
      return;
    }

    setIsSearching(true);
    setError('');
    setHasSearched(true);

    try {
      const results = await onSearchByName(searchTerm.trim());
      setSearchResults(results || []);
    } catch (error) {
      console.error('Search failed:', error);
      setError('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (error) {
      setError('');
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setHasSearched(false);
    setError('');
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content group-search-modal">
        <div className="modal-header">
          <h3>Advanced Group Search</h3>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="search-content">
          <form onSubmit={handleSearch} className="search-form">
            <div className="form-group">
              <label htmlFor="searchTerm" className="form-label">
                Search by Group Name
              </label>
              <div className="search-input-group">
                <input
                  type="text"
                  id="searchTerm"
                  value={searchTerm}
                  onChange={handleInputChange}
                  className={`form-input ${error ? 'error' : ''}`}
                  placeholder="Enter group name to search..."
                  disabled={isSearching}
                />
                <button
                  type="submit"
                  className="btn btn-primary search-btn"
                  disabled={isSearching || !searchTerm.trim()}
                >
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
              </div>
              {error && (
                <span className="error-message">{error}</span>
              )}
            </div>
          </form>

          {isSearching && (
            <div className="search-loading">
              <LoadingSpinner message="Searching groups..." />
            </div>
          )}

          {hasSearched && !isSearching && (
            <div className="search-results">
              <div className="results-header">
                <h4>Search Results</h4>
                {searchResults.length > 0 && (
                  <span className="results-count">
                    {searchResults.length} group(s) found
                  </span>
                )}
                <button 
                  className="btn btn-link clear-btn"
                  onClick={clearSearch}
                >
                  Clear
                </button>
              </div>

              {searchResults.length === 0 ? (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <p>No groups found matching "{searchTerm}"</p>
                  <p className="no-results-hint">
                    Try searching with a different term or check the spelling.
                  </p>
                </div>
              ) : (
                <div className="results-list">
                  {searchResults.map((group, index) => (
                    <div key={index} className="result-item">
                      <div className="result-header">
                        <h5 className="group-name">{group.cn}</h5>
                        {group.gidNumber && (
                          <span className="group-gid">GID: {group.gidNumber}</span>
                        )}
                      </div>
                      
                      {group.description && (
                        <p className="group-description">{group.description}</p>
                      )}
                      
                      <div className="group-details">
                        <div className="detail-item">
                          <span className="detail-label">Members:</span>
                          <span className="detail-value">
                            {group.member ? group.member.length : 0}
                          </span>
                        </div>
                        
                        <div className="detail-item">
                          <span className="detail-label">Subgroups:</span>
                          <span className="detail-value">
                            {group.memberof ? group.memberof.length : 0}
                          </span>
                        </div>
                      </div>

                      {group.member && group.member.length > 0 && (
                        <div className="members-preview">
                          <span className="members-label">Members:</span>
                          <span className="members-list">
                            {group.member.length <= 3 
                              ? group.member.join(', ')
                              : `${group.member.slice(0, 3).join(', ')} +${group.member.length - 3} more`
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupSearchModal;
